import React, { useState, useRef, useContext, createContext } from 'react';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  Button,
  Collapse,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from 'react-query';
import { client } from 'src/utils/api-client';
import { useTranslation } from 'react-i18next';

export const AIAnswerImproverContext = createContext({ isAvailable: false });

export function AIAnswerImprover(props: {
  fieldAnswerId?: number;
  setAnswer: (answer: string) => void;
}) {
  const { isAvailable } = useContext(AIAnswerImproverContext);
  const [isOpen, setIsOpen] = useState(false);
  const anchorEl = useRef(null);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [enhancedAnswer, setEnhancedAnswer] = useState<string | null>(null);

  const { t } = useTranslation();

  const enhance = useMutation(
    () =>
      client
        .post<string>(
          `/api-questionnaires/field-answers/${props.fieldAnswerId}/improve`,
          { user_prompt: prompt }
        )
        .then((res) => res.data),
    {
      onSuccess: setEnhancedAnswer,
    }
  );

  function clear() {
    setPrompt(null);
    setEnhancedAnswer(null);
    setIsOpen(false);
    enhance.reset();
  }

  if (!isAvailable) {
    return null;
  }

  const isDisabled = props.fieldAnswerId === undefined;

  return (
    <>
      <Tooltip
        title={
          isDisabled ? t('esg:Qv3AiAnswerImprover.createAnswerFirst') : null
        }
      >
        <IconButton
          onClick={() => !isDisabled && setIsOpen(true)}
          ref={anchorEl}
        >
          <SmartToyIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <DialogTitle variant="h4">
          {t('esg:Qv3AiAnswerImprover.title')}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          <TextField
            sx={{ width: '100%' }}
            label={t('esg:Qv3AiAnswerImprover.guidelines')}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
          <Collapse in={!enhance.isIdle}>
            <Typography>
              {t('esg:Qv3AiAnswerImprover.enhancedAnswer')}:{' '}
              {enhancedAnswer ?? '...'}
            </Typography>
          </Collapse>
          <Typography>{t('esg:Qv3AiAnswerImprover.note')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              props.setAnswer(enhancedAnswer!);
              clear();
            }}
            disabled={enhancedAnswer === null}
            variant="contained"
          >
            {t('esg:Qv3AiAnswerImprover.paste')}
          </Button>
          <Button
            onClick={() => {
              enhance.mutate();
              setEnhancedAnswer(null);
            }}
            disabled={prompt === null}
            variant="contained"
          >
            {t('esg:Qv3AiAnswerImprover.generate')}
          </Button>
        </DialogActions>
      </Popover>
    </>
  );
}
